import { ChannelProtocolSettings } from '~/components/channel-protocol-settings';
import * as React from 'react';
import { Link } from '@tanstack/react-router';
import {
  useEnableCameras,
  useImportNvrCameras,
  useProbeNvr,
  useRegisterManualCamera,
  type ManualCameraInput,
  type NvrProbeResult,
} from '@cguard/api-client/hooks/video-console';
import {
  useCreateSite,
  useCreateSsu,
  useSiteList,
  useSsuList,
  type SiteProvisioningInput,
  type SsuSummary,
} from '@cguard/api-client/hooks/sites';
import { hasPermission, useSession } from '@cguard/auth';
import {
  Checkbox,
  Input,
  PageContainer,
  PageHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@cguard/design-system';
import type { CameraId, SiteId, SspId, SsuId } from '@cguard/types';
import {
  Building2,
  Check,
  ChevronRight,
  CircleAlert,
  Eye,
  EyeOff,
  HardDrive,
  KeyRound,
  LoaderCircle,
  MapPinned,
  Plus,
  Radar,
  ServerCog,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AdminGuard } from '~/components/admin-guard';
import { readActionError } from '~/lib/action-error';
import { withoutSeededDemoSites, withoutSeededDemoSsus } from '~/lib/production-roster';

const ONBOARDING_STEPS = ['sites', 'recorder', 'manual'] as const;

type OnboardingMode = (typeof ONBOARDING_STEPS)[number];

type Emirate = SiteProvisioningInput['emirate'];
type SiteTier = SiteProvisioningInput['tier'];
type BillingTier = SiteProvisioningInput['billingTier'];

const EMIRATES: readonly Emirate[] = [
  'AbuDhabi',
  'Dubai',
  'Sharjah',
  'Ajman',
  'UmmAlQuwain',
  'RasAlKhaimah',
  'Fujairah',
];
const SITE_TIERS: readonly SiteTier[] = ['HighRisk', 'Commercial'];
const BILLING_TIERS: readonly BillingTier[] = ['Tier1', 'Tier2', 'Tier3'];
const CODECS: ReadonlyArray<{ value: ManualCameraInput['codecMain']; label: string }> = [
  { value: 'H264', label: 'H.264' },
  { value: 'H265', label: 'H.265' },
  { value: 'Av1', label: 'AV1' },
];

type RetainedOnboardingRequest =
  | { readonly kind: 'site' }
  | { readonly kind: 'manual'; readonly siteId: SiteId };

type ManualOnboardingAttempt =
  | { readonly kind: 'submitted'; readonly input: Readonly<ManualCameraInput> }
  | {
      readonly kind: 'registered';
      readonly cameraId: CameraId;
      readonly siteId: SiteId;
      readonly name: string;
      readonly playbackAvailable: boolean;
    };

interface OnboardedCamera {
  readonly cameraId: CameraId;
  readonly name: string;
  readonly playbackAvailable: boolean;
}

export function CameraAdminPage(): React.JSX.Element {
  return (
    <AdminGuard>
      <CameraAdministration />
    </AdminGuard>
  );
}

function CameraAdministration(): React.JSX.Element {
  const { t } = useTranslation('video');
  const sites = useSiteList();
  const availableSites = withoutSeededDemoSites(sites.data ?? []);
  // Start on recorder discovery once a site exists; a fresh account starts by adding a site.
  // The first resolved default is pinned so a later site refetch never switches the view.
  const defaultMode: OnboardingMode | null = sites.isLoading
    ? null
    : availableSites.length > 0
      ? 'recorder'
      : 'sites';
  const [pinnedMode, setMode] = React.useState<OnboardingMode | null>(null);
  if (pinnedMode === null && defaultMode !== null) setMode(defaultMode);
  const mode = pinnedMode ?? defaultMode;
  const [selectedSite, setSelectedSite] = React.useState<SiteId | null>(null);
  const [retainedRequest, setRetainedRequest] = React.useState<RetainedOnboardingRequest | null>(
    null,
  );
  const retainedRequestRef = React.useRef<RetainedOnboardingRequest | null>(null);
  const activeSite =
    retainedRequest?.kind === 'manual'
      ? retainedRequest.siteId
      : (selectedSite ?? availableSites[0]?.id ?? null);
  const onboardingRequestLocked = retainedRequest !== null;

  const retainRequest = (request: RetainedOnboardingRequest | null): void => {
    retainedRequestRef.current = request;
    setRetainedRequest(request);
  };

  const selectMode = (nextMode: OnboardingMode): void => {
    if (retainedRequestRef.current !== null) return;
    setMode(nextMode);
  };

  const selectSite = (siteId: SiteId): void => {
    if (retainedRequestRef.current !== null) return;
    setSelectedSite(siteId);
  };

  return (
    <div className="admin-page">
      <PageContainer className="mx-auto max-w-[1180px]">
        <PageHeader
          breadcrumb={t('admin.restricted')}
          title={t('admin.cameras.title')}
          description={t('admin.cameras.description')}
          actions={
            <SelectField
              className="admin-field site-picker"
              label={t('admin.cameras.site.label')}
              items={availableSites.map((site) => ({ value: site.id, label: site.name }))}
              value={activeSite}
              onValueChange={(siteId) => selectSite(siteId as SiteId)}
              placeholder={t('admin.cameras.site.none')}
              disabled={sites.isLoading || availableSites.length === 0 || onboardingRequestLocked}
            />
          }
        />

        <ChannelProtocolSettings siteId={activeSite} />

        {sites.isError ? (
          <div
            className="inline-notice danger"
            role="alert"
            data-cguard-state="error"
            data-cguard-surface="camera-directory"
          >
            <CircleAlert aria-hidden size={15} />
            <span>{t('admin.cameras.site.error')}</span>
          </div>
        ) : sites.isLoading ? (
          <div
            className="inline-notice"
            role="status"
            data-cguard-state="loading"
            data-cguard-surface="camera-directory"
          >
            <LoaderCircle className="animate-spin" aria-hidden size={15} />
            <span>{t('admin.cameras.site.loading')}</span>
          </div>
        ) : null}

        <div className="admin-layout">
          <aside className="admin-step-rail">
            {ONBOARDING_STEPS.map((step, index) => (
              <button
                key={step}
                type="button"
                className={mode === step ? 'is-active' : ''}
                onClick={() => selectMode(step)}
                disabled={onboardingRequestLocked}
              >
                <span className="step-number">{String(index + 1).padStart(2, '0')}</span>
                <span>
                  <strong>{t(`admin.cameras.steps.${step}.title`)}</strong>
                  <small>{t(`admin.cameras.steps.${step}.detail`)}</small>
                </span>
                <ChevronRight aria-hidden size={15} />
              </button>
            ))}
            <div className="admin-note">
              <KeyRound aria-hidden size={15} />
              <p>{t('admin.cameras.credentialsNote')}</p>
            </div>
          </aside>

          <section className="admin-workbench">
            {mode === null ? null : mode === 'sites' ? (
              <SiteOnboarding
                activeSiteId={activeSite}
                onSiteCreated={(siteId) => {
                  setSelectedSite(siteId);
                  setMode('recorder');
                }}
                onRequestRetainedChange={retainRequest}
              />
            ) : mode === 'recorder' ? (
              <RecorderOnboarding siteId={activeSite} />
            ) : (
              <ManualOnboarding siteId={activeSite} onRequestRetainedChange={retainRequest} />
            )}
          </section>
        </div>
      </PageContainer>
    </div>
  );
}

function SiteOnboarding({
  activeSiteId,
  onSiteCreated,
  onRequestRetainedChange,
}: {
  activeSiteId: SiteId | null;
  onSiteCreated: (siteId: SiteId) => void;
  onRequestRetainedChange: (request: RetainedOnboardingRequest | null) => void;
}): React.JSX.Element {
  const { t } = useTranslation('video');
  const session = useSession();
  const anchoredGrant = session?.scopeGrants.find(
    (grant) => grant.granteeSspId !== null || grant.granteeSsuId !== null,
  );
  const sspId = (anchoredGrant?.granteeSspId ?? null) as SspId | null;
  const scopedSsuId = (anchoredGrant?.granteeSsuId ?? null) as SsuId | null;
  const canManageSites = hasPermission(session, 'sites.manage');
  const canOverrideSsus = hasPermission(session, 'tenants.ssus.override');
  const ssus = useSsuList(sspId ?? undefined);
  const availableSsus = withoutSeededDemoSsus(ssus.data ?? []);
  const createSite = useCreateSite();
  const createSsu = useCreateSsu();
  const [showCustomerForm, setShowCustomerForm] = React.useState(false);
  const [selectedSsuId, setSelectedSsuId] = React.useState<SsuId | null>(null);
  const activeSsuId = scopedSsuId ?? selectedSsuId ?? availableSsus[0]?.id ?? null;
  const [createdSiteName, setCreatedSiteName] = React.useState('');
  const [siteSubmission, setSiteSubmission] =
    React.useState<Readonly<SiteProvisioningInput> | null>(null);
  const siteSubmissionRef = React.useRef<Readonly<SiteProvisioningInput> | null>(null);
  const submissionFence = React.useRef(false);

  const releaseSiteSubmission = (): void => {
    submissionFence.current = false;
    siteSubmissionRef.current = null;
    setSiteSubmission(null);
    onRequestRetainedChange(null);
  };

  const discardSiteSubmission = (): void => {
    if (createSite.isPending) return;
    releaseSiteSubmission();
    createSite.reset();
  };

  const submitSite = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (!canManageSites || submissionFence.current) return;
    const formElement = event.currentTarget;
    let input = siteSubmissionRef.current;
    if (!input) {
      if (!activeSsuId) return;
      const form = new FormData(formElement);
      input = {
        idempotencyKey: globalThis.crypto.randomUUID(),
        owningSsuId: activeSsuId,
        name: String(form.get('name') ?? '').trim(),
        addressLine1: String(form.get('addressLine1') ?? '').trim(),
        addressLine2: String(form.get('addressLine2') ?? '').trim(),
        city: String(form.get('city') ?? '').trim(),
        emirate: String(form.get('emirate') ?? 'Dubai') as Emirate,
        latitude: Number(form.get('latitude')),
        longitude: Number(form.get('longitude')),
        tier: String(form.get('tier') ?? 'HighRisk') as SiteTier,
        billingTier: String(form.get('billingTier') ?? 'Tier1') as BillingTier,
      } satisfies SiteProvisioningInput;
      siteSubmissionRef.current = input;
      setSiteSubmission(input);
      onRequestRetainedChange({ kind: 'site' });
    }
    submissionFence.current = true;
    createSite.mutate(input, {
      onSuccess: (site) => {
        releaseSiteSubmission();
        setCreatedSiteName(site.name);
        formElement.reset();
        onSiteCreated(site.id);
      },
      onError: (error) => {
        submissionFence.current = false;
        if (isDefinitiveValidationError(error)) {
          siteSubmissionRef.current = null;
          setSiteSubmission(null);
          onRequestRetainedChange(null);
        }
      },
    });
  };

  const submitCustomer = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (!canOverrideSsus || !sspId || scopedSsuId) return;
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    createSsu.mutate(
      {
        owningSspId: sspId,
        legalName: String(form.get('legalName') ?? '').trim(),
        tradeName: String(form.get('tradeName') ?? '').trim(),
        contactEmail: String(form.get('contactEmail') ?? '').trim(),
        billingAddress: String(form.get('billingAddress') ?? '').trim(),
        contractStartUtc: new Date(
          `${String(form.get('contractStart') ?? '')}T00:00:00.000Z`,
        ).toISOString(),
        reason: String(form.get('reason') ?? '').trim(),
      },
      {
        onSuccess: (ssu) => {
          setSelectedSsuId(ssu.id);
          setShowCustomerForm(false);
          formElement.reset();
        },
      },
    );
  };

  const siteRequestLocked = createSite.isPending || siteSubmission !== null;

  return (
    <div
      className="workbench-stack"
      data-cguard-state={ssus.isError ? 'error' : ssus.isLoading ? 'loading' : 'ready'}
      data-cguard-surface="camera-administration"
    >
      <div className="workbench-title">
        <span className="workbench-icon">
          <MapPinned aria-hidden size={20} />
        </span>
        <div>
          <p className="eyebrow">{t('admin.cameras.siteForm.eyebrow')}</p>
          <h2>{t('admin.cameras.siteForm.title')}</h2>
          <p>
            {t('admin.cameras.siteForm.description')}
            {activeSiteId ? ` ${t('admin.cameras.siteForm.selectedContext')}` : null}
          </p>
        </div>
      </div>

      {ssus.isLoading ? (
        <div className="inline-notice" role="status">
          <LoaderCircle className="animate-spin" aria-hidden size={15} />
          <span>{t('admin.cameras.siteForm.customersLoading')}</span>
        </div>
      ) : ssus.isError ? (
        <InlineNotice tone="danger" icon={<CircleAlert aria-hidden size={15} />}>
          {t('admin.cameras.siteForm.customersError')}
        </InlineNotice>
      ) : null}

      {createdSiteName ? (
        <InlineNotice tone="success" icon={<Check aria-hidden size={15} />}>
          {t('admin.cameras.siteForm.created', { name: createdSiteName })}
        </InlineNotice>
      ) : null}

      {scopedSsuId === null && canOverrideSsus ? (
        <div
          className="setup-switcher"
          role="group"
          aria-label={t('admin.cameras.siteForm.ownership')}
        >
          <button
            type="button"
            className={!showCustomerForm ? 'is-active' : ''}
            onClick={() => setShowCustomerForm(false)}
            disabled={siteRequestLocked}
          >
            {t('admin.cameras.siteForm.addSite')}
          </button>
          <button
            type="button"
            className={showCustomerForm ? 'is-active' : ''}
            onClick={() => setShowCustomerForm(true)}
            disabled={siteRequestLocked}
          >
            {t('admin.cameras.customerForm.title')}
          </button>
        </div>
      ) : null}

      {showCustomerForm && scopedSsuId === null && canOverrideSsus ? (
        <form className="site-form" onSubmit={submitCustomer}>
          <div className="form-section-heading">
            <Building2 aria-hidden size={16} />
            <div>
              <strong>{t('admin.cameras.customerForm.title')}</strong>
              <small>{t('admin.cameras.customerForm.hint')}</small>
            </div>
          </div>
          <Field label={t('admin.cameras.customerForm.legalName')}>
            <Input name="legalName" required autoComplete="organization" />
          </Field>
          <Field label={t('admin.cameras.customerForm.tradeName')}>
            <Input name="tradeName" required autoComplete="organization" />
          </Field>
          <Field label={t('admin.cameras.customerForm.contactEmail')}>
            <Input name="contactEmail" type="email" required autoComplete="email" />
          </Field>
          <Field label={t('admin.cameras.customerForm.contractStart')}>
            <Input
              name="contractStart"
              type="date"
              required
              defaultValue={new Date().toISOString().slice(0, 10)}
            />
          </Field>
          <Field label={t('admin.cameras.customerForm.billingAddress')}>
            <Input name="billingAddress" required autoComplete="street-address" />
          </Field>
          <Field label={t('admin.cameras.customerForm.reason')}>
            <Textarea name="reason" required maxLength={500} />
          </Field>
          {!sspId ? (
            <InlineNotice tone="danger" icon={<CircleAlert aria-hidden size={15} />}>
              {t('admin.cameras.customerForm.noScope')}
            </InlineNotice>
          ) : null}
          {createSsu.isError ? (
            <InlineNotice tone="danger" icon={<CircleAlert aria-hidden size={15} />}>
              {readActionError(createSsu.error, t('admin.cameras.customerForm.createFailed'))}
            </InlineNotice>
          ) : null}
          <button
            className="primary-button justify-self-start"
            type="submit"
            disabled={createSsu.isPending || !sspId}
          >
            {createSsu.isPending ? (
              <LoaderCircle className="animate-spin" aria-hidden size={15} />
            ) : (
              <Plus aria-hidden size={15} />
            )}
            {createSsu.isPending
              ? t('admin.cameras.customerForm.creating')
              : t('admin.cameras.customerForm.create')}
          </button>
        </form>
      ) : (
        <form className="site-form" onSubmit={submitSite}>
          <fieldset disabled={siteRequestLocked || !canManageSites} style={{ display: 'contents' }}>
            <div className="form-section-heading">
              <MapPinned aria-hidden size={16} />
              <div>
                <strong>{t('admin.cameras.siteForm.details')}</strong>
                <small>{t('admin.cameras.siteForm.detailsHint')}</small>
              </div>
            </div>
            <SelectField
              label={t('admin.cameras.siteForm.customer')}
              items={availableSsus.map((ssu: SsuSummary) => ({
                value: ssu.id,
                label: ssu.tradeName,
              }))}
              value={activeSsuId}
              onValueChange={(ssuId) => setSelectedSsuId(ssuId as SsuId)}
              placeholder={t('admin.cameras.siteForm.noCustomer')}
              required
              disabled={ssus.isLoading || availableSsus.length === 0}
            />
            <Field label={t('admin.cameras.siteForm.name')}>
              <Input name="name" required />
            </Field>
            <Field label={t('admin.cameras.siteForm.address')}>
              <Input name="addressLine1" required autoComplete="address-line1" />
            </Field>
            <Field label={t('admin.cameras.siteForm.address2')} hint={t('admin.cameras.optional')}>
              <Input name="addressLine2" autoComplete="address-line2" />
            </Field>
            <Field label={t('admin.cameras.siteForm.city')}>
              <Input name="city" required autoComplete="address-level2" />
            </Field>
            <SelectField
              label={t('admin.cameras.siteForm.emirate')}
              name="emirate"
              items={EMIRATES.map((emirate) => ({
                value: emirate,
                label: t(`admin.cameras.siteForm.emirates.${emirate}`),
              }))}
              defaultValue="Dubai"
            />
            <Field
              label={t('admin.cameras.siteForm.latitude')}
              hint={t('admin.cameras.siteForm.coordinateHint', { example: '25.0800' })}
            >
              <Input
                name="latitude"
                type="number"
                min="-90"
                max="90"
                step="any"
                required
                inputMode="decimal"
              />
            </Field>
            <Field
              label={t('admin.cameras.siteForm.longitude')}
              hint={t('admin.cameras.siteForm.coordinateHint', { example: '55.2708' })}
            >
              <Input
                name="longitude"
                type="number"
                min="-180"
                max="180"
                step="any"
                required
                inputMode="decimal"
              />
            </Field>
            <SelectField
              label={t('admin.cameras.siteForm.classification')}
              name="tier"
              items={SITE_TIERS.map((tier) => ({
                value: tier,
                label: t(`admin.cameras.siteForm.tiers.${tier}`),
              }))}
              defaultValue="HighRisk"
            />
            <SelectField
              label={t('admin.cameras.siteForm.billingTier')}
              name="billingTier"
              items={BILLING_TIERS.map((tier, index) => ({
                value: tier,
                label: t('admin.cameras.siteForm.billingTierOption', { number: index + 1 }),
              }))}
              defaultValue="Tier1"
            />
          </fieldset>
          {ssus.isError ? (
            <InlineNotice tone="danger" icon={<CircleAlert aria-hidden size={15} />}>
              {readActionError(ssus.error, t('admin.cameras.siteForm.customersUnavailable'))}
            </InlineNotice>
          ) : null}
          {!canManageSites ? (
            <InlineNotice tone="danger" icon={<CircleAlert aria-hidden size={15} />}>
              {t('admin.cameras.siteForm.noPermission')}
            </InlineNotice>
          ) : null}
          {createSite.isError ? (
            <InlineNotice tone="danger" icon={<CircleAlert aria-hidden size={15} />}>
              {readActionError(createSite.error, t('admin.cameras.siteForm.createFailed'))}
            </InlineNotice>
          ) : null}
          <button
            className="primary-button justify-self-start"
            type="submit"
            disabled={!canManageSites || createSite.isPending || (!activeSsuId && !siteSubmission)}
          >
            {createSite.isPending ? (
              <LoaderCircle className="animate-spin" aria-hidden size={15} />
            ) : (
              <Plus aria-hidden size={15} />
            )}
            {createSite.isPending
              ? t('admin.cameras.siteForm.creating')
              : siteSubmission
                ? t('admin.cameras.siteForm.retry')
                : t('admin.cameras.siteForm.create')}
          </button>
          {siteSubmission && !createSite.isPending ? (
            <button
              className="secondary-button justify-self-start"
              type="button"
              onClick={discardSiteSubmission}
            >
              {t('admin.cameras.siteForm.discard')}
            </button>
          ) : null}
        </form>
      )}
    </div>
  );
}

function RecorderOnboarding({ siteId }: { siteId: SiteId | null }): React.JSX.Element {
  const { t } = useTranslation('video');
  const probe = useProbeNvr();
  const importer = useImportNvrCameras();
  const enableCameras = useEnableCameras();
  const [address, setAddress] = React.useState('');
  const [username, setUsername] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [probeResult, setProbeResult] = React.useState<NvrProbeResult | null>(null);
  const [probeError, setProbeError] = React.useState<Error | null>(null);
  const [selection, setSelection] = React.useState<Set<string>>(new Set());
  const [importedCameras, setImportedCameras] = React.useState<ReadonlyArray<OnboardedCamera>>([]);
  const [inactiveCameras, setInactiveCameras] = React.useState<ReadonlyArray<OnboardedCamera>>([]);

  const enableImported = (cameras: ReadonlyArray<OnboardedCamera>): void => {
    setInactiveCameras(cameras);
    enableCameras.mutate(
      cameras.map((camera) => camera.cameraId),
      {
        onSuccess: () => {
          setInactiveCameras([]);
          setImportedCameras(cameras);
        },
      },
    );
  };

  const runProbe = (event: React.FormEvent): void => {
    event.preventDefault();
    if (!siteId) return;
    setImportedCameras([]);
    setInactiveCameras([]);
    enableCameras.reset();
    setProbeResult(null);
    setProbeError(null);
    setSelection(new Set());
    const submittedPassword = password;
    setPassword('');
    probe.mutate(
      { siteId, address, username, password: submittedPassword },
      {
        onSuccess: (result) => {
          setProbeResult(result);
          setSelection(new Set(result.cameras.map((camera) => camera.key)));
        },
        onError: (error) => setProbeError(error),
        onSettled: () => probe.reset(),
      },
    );
  };

  const importSelection = (): void => {
    if (!siteId || !probeResult || selection.size === 0) return;
    importer.mutate(
      {
        siteId,
        sessionId: probeResult.sessionId,
        cameraKeys: Array.from(selection),
      },
      {
        // The discovery session is spent: drop its channel list so nothing can be added twice.
        onSuccess: (cameras) => {
          setProbeResult(null);
          setSelection(new Set());
          enableImported(
            cameras.map((camera) => ({
              cameraId: camera.id,
              name: camera.name,
              playbackAvailable: camera.playbackConfigured,
            })),
          );
        },
      },
    );
  };

  return (
    <div
      className="workbench-stack"
      data-cguard-state="ready"
      data-cguard-surface="camera-administration"
    >
      <div className="workbench-title">
        <span className="workbench-icon">
          <Radar aria-hidden size={20} />
        </span>
        <div>
          <p className="eyebrow">{t('admin.cameras.recorder.eyebrow')}</p>
          <h2>{t('admin.cameras.recorder.title')}</h2>
          <p>{t('admin.cameras.recorder.description')}</p>
        </div>
      </div>

      <form className="nvr-form" onSubmit={runProbe}>
        <Field label={t('admin.cameras.recorder.address')}>
          <Input
            required
            value={address}
            onChange={(event) => setAddress(event.target.value)}
            placeholder={t('admin.cameras.recorder.addressPlaceholder')}
            autoComplete="off"
          />
        </Field>
        <Field label={t('admin.cameras.recorder.username')}>
          <Input
            required
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            autoComplete="username"
          />
        </Field>
        <Field label={t('admin.cameras.recorder.password')}>
          <Input
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            type="password"
            autoComplete="new-password"
          />
        </Field>
        <button
          className="primary-button self-end"
          type="submit"
          disabled={probe.isPending || !siteId}
        >
          {probe.isPending ? (
            <LoaderCircle className="animate-spin" aria-hidden size={15} />
          ) : (
            <Radar aria-hidden size={15} />
          )}
          {probe.isPending
            ? t('admin.cameras.recorder.discovering')
            : t('admin.cameras.recorder.find')}
        </button>
      </form>

      {!siteId ? (
        <InlineNotice tone="danger" icon={<CircleAlert aria-hidden size={15} />}>
          {t('admin.cameras.recorder.noSite')}
        </InlineNotice>
      ) : null}
      {probeError ? (
        <InlineNotice tone="danger" icon={<CircleAlert aria-hidden size={15} />}>
          {readActionError(probeError, t('admin.cameras.recorder.probeFailed'))}
        </InlineNotice>
      ) : null}
      {importer.isError ? (
        <InlineNotice tone="danger" icon={<CircleAlert aria-hidden size={15} />}>
          {readActionError(importer.error, t('admin.cameras.recorder.importFailed'))}
        </InlineNotice>
      ) : null}
      {enableCameras.isError && inactiveCameras.length > 0 ? (
        <InlineNotice tone="danger" icon={<CircleAlert aria-hidden size={15} />}>
          {readActionError(enableCameras.error, t('admin.cameras.recorder.enableFailed'))}{' '}
          <button
            type="button"
            className="text-button"
            onClick={() => enableImported(inactiveCameras)}
          >
            {t('admin.cameras.recorder.switchOn')}
          </button>
        </InlineNotice>
      ) : null}
      {importedCameras.length > 0 ? (
        <InlineNotice tone="success" icon={<Check aria-hidden size={15} />}>
          {t('admin.cameras.recorder.added', { count: importedCameras.length })}
        </InlineNotice>
      ) : null}
      {importedCameras.length > 0 ? <OnboardingSuccessActions cameras={importedCameras} /> : null}

      {probeResult ? (
        <CandidateSelection
          probe={probeResult}
          selection={selection}
          onSelection={setSelection}
          pending={importer.isPending || enableCameras.isPending}
          onImport={importSelection}
        />
      ) : importedCameras.length > 0 || inactiveCameras.length > 0 ? null : (
        <div className="discovery-empty">
          <HardDrive aria-hidden size={25} strokeWidth={1.4} />
          <h3>{t('admin.cameras.recorder.empty.title')}</h3>
          <p>{t('admin.cameras.recorder.empty.detail')}</p>
        </div>
      )}
    </div>
  );
}

function CandidateSelection({
  probe,
  selection,
  onSelection,
  pending,
  onImport,
}: {
  probe: NvrProbeResult;
  selection: Set<string>;
  onSelection: (selection: Set<string>) => void;
  pending: boolean;
  onImport: () => void;
}): React.JSX.Element {
  const { t } = useTranslation('video');
  const toggle = (key: string): void => {
    const next = new Set(selection);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onSelection(next);
  };

  return (
    <div className="candidate-panel">
      <header>
        <div className="flex items-center gap-3">
          <span className="device-mark">
            <ServerCog aria-hidden size={17} />
          </span>
          <div>
            <h3>
              {probe.device.vendor} {probe.device.model}
            </h3>
            <p>
              {t('admin.cameras.candidates.channels', {
                count: probe.cameras.length,
                method:
                  probe.method === 'onvif'
                    ? t('admin.cameras.candidates.onvif')
                    : t('admin.cameras.candidates.vendor'),
              })}
            </p>
          </div>
        </div>
        <button
          type="button"
          className="text-button"
          onClick={() =>
            onSelection(
              selection.size === probe.cameras.length
                ? new Set()
                : new Set(probe.cameras.map((camera) => camera.key)),
            )
          }
        >
          {selection.size === probe.cameras.length
            ? t('admin.cameras.candidates.clearAll')
            : t('admin.cameras.candidates.selectAll')}
        </button>
      </header>
      <div className="candidate-list">
        {probe.cameras.map((camera) => (
          <label key={camera.key} className="candidate-row">
            <Checkbox
              checked={selection.has(camera.key)}
              onCheckedChange={() => toggle(camera.key)}
            />
            <span className="channel-number">{String(camera.channelNumber).padStart(2, '0')}</span>
            <span className="min-w-0 flex-1">
              <strong>{camera.name}</strong>
              <small>
                {camera.resolution} · {camera.codec}
              </small>
            </span>
            <span className={camera.playbackAvailable ? 'source-ready' : 'source-limited'}>
              {camera.playbackAvailable
                ? t('admin.cameras.candidates.livePlayback')
                : t('admin.cameras.candidates.liveOnly')}
            </span>
          </label>
        ))}
      </div>
      <footer>
        <span>{t('admin.cameras.candidates.selected', { count: selection.size })}</span>
        <button
          type="button"
          className="primary-button"
          onClick={onImport}
          disabled={selection.size === 0 || pending}
        >
          {pending ? (
            <LoaderCircle className="animate-spin" aria-hidden size={15} />
          ) : (
            <Plus aria-hidden size={15} />
          )}
          {pending ? t('admin.cameras.candidates.adding') : t('admin.cameras.candidates.add')}
        </button>
      </footer>
    </div>
  );
}

function OnboardingSuccessActions({
  cameras,
}: {
  readonly cameras: ReadonlyArray<OnboardedCamera>;
}): React.JSX.Element {
  const { t } = useTranslation('video');
  return (
    <ul className="candidate-list" aria-label={t('admin.cameras.success.label')}>
      {cameras.map((camera) => (
        <li key={camera.cameraId} className="candidate-row">
          <span className="min-w-0 flex-1">
            <strong>{camera.name}</strong>
            <small>
              {camera.playbackAvailable
                ? t('admin.cameras.success.waitingPlayback')
                : t('admin.cameras.success.waiting')}
            </small>
          </span>
          <Link to="/live" search={{ cameraId: camera.cameraId }} className="secondary-button">
            {t('viewer.viewLive')}
          </Link>
          {camera.playbackAvailable ? (
            <Link
              to="/playback"
              search={{ cameraId: camera.cameraId }}
              className="secondary-button"
            >
              {t('live.openPlayback')}
            </Link>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

function ManualOnboarding({
  siteId,
  onRequestRetainedChange,
}: {
  siteId: SiteId | null;
  onRequestRetainedChange: (request: RetainedOnboardingRequest | null) => void;
}): React.JSX.Element {
  const { t } = useTranslation('video');
  const register = useRegisterManualCamera();
  const enableCameras = useEnableCameras();
  // Remounting the form resets every field, the design-system selects included.
  const [formKey, setFormKey] = React.useState(0);
  const [savedCamera, setSavedCamera] = React.useState<OnboardedCamera | null>(null);
  const [manualAttempt, setManualAttempt] = React.useState<ManualOnboardingAttempt | null>(null);
  const [registrationError, setRegistrationError] = React.useState<Error | null>(null);
  const manualAttemptRef = React.useRef<ManualOnboardingAttempt | null>(null);
  const submissionFence = React.useRef(false);

  const retainManualAttempt = (attempt: ManualOnboardingAttempt): void => {
    manualAttemptRef.current = attempt;
    setManualAttempt(attempt);
    onRequestRetainedChange({
      kind: 'manual',
      siteId: attempt.kind === 'submitted' ? attempt.input.siteId : attempt.siteId,
    });
  };

  const releaseManualAttempt = (): void => {
    submissionFence.current = false;
    manualAttemptRef.current = null;
    setManualAttempt(null);
    onRequestRetainedChange(null);
  };

  const abandonManualAttempt = (): void => {
    if (register.isPending || enableCameras.isPending) return;
    setFormKey((key) => key + 1);
    setRegistrationError(null);
    releaseManualAttempt();
    register.reset();
    enableCameras.reset();
  };

  const activateRegisteredCamera = (
    attempt: Extract<ManualOnboardingAttempt, { kind: 'registered' }>,
  ): void => {
    if (submissionFence.current) return;
    submissionFence.current = true;
    enableCameras.mutate([attempt.cameraId], {
      onSuccess: () => {
        releaseManualAttempt();
        setFormKey((key) => key + 1);
        setSavedCamera({
          cameraId: attempt.cameraId,
          name: attempt.name,
          playbackAvailable: attempt.playbackAvailable,
        });
      },
      onError: () => {
        submissionFence.current = false;
      },
    });
  };

  const submit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (submissionFence.current) return;
    const formElement = event.currentTarget;
    const retainedAttempt = manualAttemptRef.current;
    if (retainedAttempt?.kind === 'registered') {
      activateRegisteredCamera(retainedAttempt);
      return;
    }

    let input = retainedAttempt?.input;
    if (!input) {
      if (!siteId) return;
      const form = new FormData(formElement);
      const subStreamUrl = String(form.get('subStreamUrl') ?? '').trim();
      const playbackUrlTemplate = String(form.get('playbackUrlTemplate') ?? '').trim();
      input = {
        idempotencyKey: globalThis.crypto.randomUUID(),
        siteId,
        name: String(form.get('name') ?? '').trim(),
        ipAddress: String(form.get('ipAddress') ?? '').trim(),
        mainStreamUrl: String(form.get('mainStreamUrl') ?? '').trim(),
        resolutionMain: String(form.get('resolutionMain') ?? '1920x1080'),
        frameRateMain: Number(form.get('frameRateMain') ?? 25),
        codecMain: String(form.get('codecMain') ?? 'H264') as ManualCameraInput['codecMain'],
        ...(subStreamUrl ? { subStreamUrl } : {}),
        ...(playbackUrlTemplate ? { playbackUrlTemplate } : {}),
      } satisfies ManualCameraInput;
      retainManualAttempt({ kind: 'submitted', input });
      clearSecretInputs(formElement);
    }
    submissionFence.current = true;
    setRegistrationError(null);
    const registeredSiteId = input.siteId;
    const registeredName = input.name;
    try {
      const camera = await register.mutateAsync(input);
      const registeredAttempt = {
        kind: 'registered',
        cameraId: camera.id,
        siteId: registeredSiteId,
        name: registeredName,
        playbackAvailable: camera.playbackConfigured,
      } satisfies ManualOnboardingAttempt;
      submissionFence.current = false;
      setRegistrationError(null);
      retainManualAttempt(registeredAttempt);
      register.reset();
      activateRegisteredCamera(registeredAttempt);
    } catch (registrationFailure) {
      const normalizedError =
        registrationFailure instanceof Error
          ? registrationFailure
          : new Error('Camera registration failed.');
      submissionFence.current = false;
      setRegistrationError(normalizedError);
      register.reset();
      if (isDefinitiveValidationError(normalizedError)) {
        // Keep the non-secret values so the operator only corrects and re-enters the streams.
        restoreManualInput(formElement, input);
        releaseManualAttempt();
      }
    }
  };

  const requestPending = register.isPending || enableCameras.isPending;
  const manualRequestLocked = requestPending || manualAttempt !== null;

  return (
    <div className="workbench-stack">
      <div className="workbench-title">
        <span className="workbench-icon">
          <Plus aria-hidden size={20} />
        </span>
        <div>
          <p className="eyebrow">{t('admin.cameras.manual.eyebrow')}</p>
          <h2>{t('admin.cameras.manual.title')}</h2>
          <p>{t('admin.cameras.manual.description')}</p>
        </div>
      </div>
      {savedCamera ? (
        <InlineNotice tone="success" icon={<Check aria-hidden size={15} />}>
          {t('admin.cameras.manual.added', { name: savedCamera.name })}
        </InlineNotice>
      ) : null}
      {savedCamera ? <OnboardingSuccessActions cameras={[savedCamera]} /> : null}
      {!siteId ? (
        <InlineNotice tone="danger" icon={<CircleAlert aria-hidden size={15} />}>
          {t('admin.cameras.manual.noSite')}
        </InlineNotice>
      ) : null}
      <form key={formKey} className="manual-form" onSubmit={submit}>
        <fieldset disabled={manualRequestLocked} style={{ display: 'contents' }}>
          <Field label={t('admin.cameras.manual.name')}>
            <Input name="name" required placeholder={t('admin.cameras.manual.namePlaceholder')} />
          </Field>
          <Field label={t('admin.cameras.manual.address')}>
            <Input name="ipAddress" required placeholder="10.24.8.31" />
          </Field>
          <SecretField
            label={t('admin.cameras.manual.mainStream')}
            hint={t('admin.cameras.manual.mainStreamHint')}
            name="mainStreamUrl"
            placeholder={t('admin.cameras.manual.mainStreamPlaceholder')}
            required
          />
          <SecretField
            label={t('admin.cameras.manual.subStream')}
            hint={t('admin.cameras.optional')}
            name="subStreamUrl"
            placeholder={t('admin.cameras.manual.subStreamPlaceholder')}
          />
          <SecretField
            label={t('admin.cameras.manual.playbackTemplate')}
            hint={t('admin.cameras.manual.playbackTemplateHint', { from: '{from}', to: '{to}' })}
            name="playbackUrlTemplate"
            placeholder={t('admin.cameras.manual.playbackTemplatePlaceholder')}
          />
          <div className="form-grid-3">
            <Field label={t('admin.cameras.manual.resolution')}>
              <Input name="resolutionMain" defaultValue="1920x1080" required />
            </Field>
            <Field label={t('admin.cameras.manual.frameRate')}>
              <Input name="frameRateMain" type="number" min="1" max="60" defaultValue="25" />
            </Field>
            <SelectField
              label={t('admin.cameras.manual.codec')}
              name="codecMain"
              items={CODECS}
              defaultValue="H264"
              disabled={manualRequestLocked}
            />
          </div>
        </fieldset>
        {registrationError ? (
          <InlineNotice tone="danger" icon={<CircleAlert aria-hidden size={15} />}>
            {readActionError(registrationError, t('admin.cameras.manual.registerFailed'))}
          </InlineNotice>
        ) : null}
        {enableCameras.isError ? (
          <InlineNotice tone="danger" icon={<CircleAlert aria-hidden size={15} />}>
            {readActionError(enableCameras.error, t('admin.cameras.manual.enableFailed'))}
          </InlineNotice>
        ) : null}
        <button
          className="primary-button justify-self-start"
          type="submit"
          disabled={requestPending || (!siteId && !manualAttempt)}
        >
          {requestPending ? (
            <LoaderCircle className="animate-spin" aria-hidden size={15} />
          ) : (
            <Plus aria-hidden size={15} />
          )}
          {enableCameras.isPending
            ? t('admin.cameras.manual.activating')
            : register.isPending
              ? t('admin.cameras.manual.adding')
              : manualAttempt?.kind === 'registered'
                ? t('admin.cameras.manual.retryActivation')
                : manualAttempt?.kind === 'submitted'
                  ? t('admin.cameras.manual.retry')
                  : t('admin.cameras.manual.add')}
        </button>
        {manualAttempt && !requestPending ? (
          <button
            className="secondary-button justify-self-start"
            type="button"
            onClick={abandonManualAttempt}
          >
            {manualAttempt.kind === 'registered'
              ? t('admin.cameras.manual.finishLater')
              : t('admin.cameras.manual.discard')}
          </button>
        ) : null}
      </form>
    </div>
  );
}

function SecretField({
  label,
  hint,
  name,
  placeholder,
  required = false,
}: {
  label: string;
  hint: string;
  name: string;
  placeholder: string;
  required?: boolean;
}): React.JSX.Element {
  const { t } = useTranslation('video');
  const [revealed, setRevealed] = React.useState(false);
  const inputId = React.useId();

  return (
    <div className="admin-field">
      <label htmlFor={inputId}>
        {label}
        <small>{hint}</small>
      </label>
      <div className="secret-field-control">
        <Input
          id={inputId}
          name={name}
          type={revealed ? 'text' : 'password'}
          required={required}
          placeholder={placeholder}
          autoComplete="off"
          spellCheck={false}
        />
        <button
          type="button"
          className="secret-reveal"
          aria-label={t(revealed ? 'admin.cameras.secret.hide' : 'admin.cameras.secret.show', {
            label,
          })}
          aria-pressed={revealed}
          onClick={() => setRevealed((current) => !current)}
        >
          {revealed ? <EyeOff aria-hidden size={15} /> : <Eye aria-hidden size={15} />}
        </button>
      </div>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <label className="admin-field">
      <span>
        {label}
        {hint ? <small>{hint}</small> : null}
      </span>
      {children}
    </label>
  );
}

function SelectField({
  className = 'admin-field',
  label,
  items,
  placeholder,
  onValueChange,
  ...props
}: {
  className?: string;
  label: string;
  items: ReadonlyArray<{ value: string; label: string }>;
  placeholder?: string;
  name?: string;
  defaultValue?: string;
  value?: string | null;
  onValueChange?: (value: string) => void;
  disabled?: boolean;
  required?: boolean;
}): React.JSX.Element {
  const id = React.useId();
  return (
    <div className={className}>
      <label htmlFor={id}>{label}</label>
      <Select
        items={items}
        {...props}
        onValueChange={(next: string | null) => {
          if (next !== null) onValueChange?.(next);
        }}
      >
        <SelectTrigger id={id}>
          <SelectValue {...(placeholder ? { placeholder } : {})} />
        </SelectTrigger>
        <SelectContent>
          {items.map((item) => (
            <SelectItem key={item.value} value={item.value}>
              {item.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function InlineNotice({
  tone,
  icon,
  children,
}: {
  tone: 'success' | 'danger';
  icon: React.ReactNode;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className={`inline-notice ${tone}`} role={tone === 'danger' ? 'alert' : 'status'}>
      {icon}
      <span>{children}</span>
    </div>
  );
}

/** Stream addresses can carry credentials, so they never stay in the form after a submit. */
const SECRET_INPUT_NAMES = ['mainStreamUrl', 'subStreamUrl', 'playbackUrlTemplate'] as const;

function clearSecretInputs(form: HTMLFormElement): void {
  for (const name of SECRET_INPUT_NAMES) {
    const input = form.elements.namedItem(name);
    if (input instanceof HTMLInputElement) input.value = '';
  }
}

function restoreManualInput(form: HTMLFormElement, input: Readonly<ManualCameraInput>): void {
  const values: Record<string, string> = {
    name: input.name,
    ipAddress: input.ipAddress,
    resolutionMain: input.resolutionMain,
    frameRateMain: String(input.frameRateMain),
  };
  for (const [name, value] of Object.entries(values)) {
    const field = form.elements.namedItem(name);
    if (field instanceof HTMLInputElement) field.value = value;
  }
  clearSecretInputs(form);
}

function isDefinitiveValidationError(error: Error): boolean {
  const apiError = error as Error & { status?: unknown; body?: unknown };
  if (apiError.status === 400 || apiError.status === 422) return true;
  if (apiError.status !== 409 || typeof apiError.body !== 'object' || apiError.body === null) {
    return false;
  }
  const code = (apiError.body as { readonly code?: unknown }).code;
  return (
    code === 'sites.site.idempotency_payload_mismatch' ||
    code === 'cameras.camera.idempotency_payload_mismatch'
  );
}
